import { useState, useEffect } from 'react'
import { apiService } from '../services/apiService'
import { passwordCache } from '../utils/passwordCache'
import ShipmentItemRow, { SHIPMENT_TABLE_GRID } from './ShipmentItemRow'
import ShipmentChecklist from './ShipmentChecklist'
import CUSMAIndicator from './CUSMAIndicator'
import CUSMAFormModal from './CUSMAFormModal'
import type { Shipment, ShipmentItem } from '../types'
import { calculatePatternEstimate } from '../utils/patternRates'
import type { PatternEstimate } from '../utils/patternRates'

interface ShippingRate {
  carrier_id: string
  service_id: string
  carrier_name: string
  service_name: string
  base_cost: number
  surcharges: { key: string; amount: number }[]
  taxes: { key: string; amount: number }[]
  total: number
  currency: string
  formatted: string
  transit_days: number
  transit_time_not_available: boolean
  paperless: boolean
  brokerage_fees_included: boolean | null
  duties_and_taxes_included: boolean | null
  valid_until: string
}

interface ShippingRatesDisplayProps {
  cheapestRate: ShippingRate
  allRates: ShippingRate[]
  locationName: string
  shipmentId: string
  selectedRate?: ShippingRate | null
  onRateSelect?: (shipmentId: string, rate: ShippingRate) => void
  onAddToCart?: (rate: ShippingRate) => void
  cogsValueUSD?: number
  lineItems?: number
  hasFood?: boolean
}

function ShippingRatesDisplay({ cheapestRate, allRates, locationName, shipmentId, selectedRate, onRateSelect, onAddToCart, cogsValueUSD = 0, lineItems = 5, hasFood = false }: ShippingRatesDisplayProps) {
  const [expanded, setExpanded] = useState(false)
  
  // Brokerage fee calculation functions (simplified from main page - ignoring duties)
  const calculateUPSBrokerageFee = (value: number) => {
    let entryPreparationFee = 0
    if (value <= 200) {
      entryPreparationFee = 17.50
    } else if (value <= 800) {
      entryPreparationFee = 30.50
    } else if (value <= 1250) {
      entryPreparationFee = 42.25
    } else if (value <= 2500) {
      entryPreparationFee = 55.75
    } else {
      const additionalThousands = Math.ceil((value - 2500) / 1000)
      entryPreparationFee = 55.75 + (additionalThousands * 1.95)
    }
    
    // FDA fee if food involved
    const fdaFee = hasFood ? 11.50 : 0
    
    // Bond fee for high value shipments
    const bondFee = value > 2500 ? Math.max(value * 0.0075, 75.00) : 0
    
    return entryPreparationFee + fdaFee + bondFee
  }
  
  const calculateFedExBrokerageFee = (lineItemCount: number) => {
    const clearanceEntryFee = 36.40
    const liveEntryFee = hasFood ? 12.00 : 0
    const additionalLineItemsFee = lineItemCount > 5 ? (lineItemCount - 5) * 5.95 : 0
    
    return clearanceEntryFee + liveEntryFee + additionalLineItemsFee
  }
  
  // Calculate effective total cost for each rate and sort by it
  const ratesWithEffectiveCost = allRates
    .filter(rate => {
      const carrierId = rate.carrier_id.toLowerCase()
      return carrierId.includes('ups') || carrierId.includes('fedex')
    })
    .map(rate => {
      const carrierId = rate.carrier_id.toLowerCase()
      let brokerageFeeUSD = 0
      
      if (!rate.brokerage_fees_included && cogsValueUSD > 0) {
        if (carrierId.includes('ups')) {
          brokerageFeeUSD = calculateUPSBrokerageFee(cogsValueUSD)
        } else if (carrierId.includes('fedex')) {
          brokerageFeeUSD = calculateFedExBrokerageFee(lineItems)
        }
      }
      
      // Convert USD brokerage to CAD
      const brokerageFeeCAD = brokerageFeeUSD * 1.35
      const effectiveTotalCost = rate.total + brokerageFeeCAD
      
      return {
        ...rate,
        brokerageFeeUSD,
        brokerageFeeCAD,
        effectiveTotalCost
      }
    })
    .sort((a, b) => a.effectiveTotalCost - b.effectiveTotalCost)
  
  const displayRate = selectedRate || (ratesWithEffectiveCost.length > 0 ? ratesWithEffectiveCost[0] : cheapestRate)

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setExpanded(false)
    if (expanded) {
      document.addEventListener('click', handleClickOutside)
      return () => document.removeEventListener('click', handleClickOutside)
    }
  }, [expanded])

  return (
    <div>
      {/* Main rate display - show total cost including brokerage in CAD */}
      <div style={{ fontSize: '14px', fontWeight: 'bold', color: selectedRate ? '#007bff' : '#28a745' }}>
        ${displayRate.effectiveTotalCost?.toFixed(2) || displayRate.total?.toFixed(2)} CAD
      </div>
      <div style={{ fontSize: '12px', color: '#666', fontWeight: 'normal', lineHeight: '1.3' }}>
        {displayRate.carrier_name} - {displayRate.service_name}
        {displayRate.transit_days && (
          <div>{displayRate.transit_days} days</div>
        )}
        {selectedRate && (
          <div style={{ color: '#007bff', fontSize: '11px' }}>✓ Selected</div>
        )}
        {/* Show breakdown if brokerage fees are added */}
        {!displayRate.brokerage_fees_included && cogsValueUSD > 0 && displayRate.brokerageFeeCAD > 0 && (
          <div style={{ color: '#666', fontSize: '10px', marginTop: '2px' }}>
            Shipping: ${displayRate.total?.toFixed(2)} CAD + Brokerage: ${displayRate.brokerageFeeCAD?.toFixed(2)} CAD
          </div>
        )}
      </div>
      
      {/* Show all rates button if there are multiple rates */}
      {ratesWithEffectiveCost.length > 1 && (
        <div style={{ marginTop: '8px' }}>
          <button
            onClick={(e) => {
              e.stopPropagation()
              setExpanded(!expanded)
            }}
            style={{
              fontSize: '12px',
              padding: '8px 12px',
              backgroundColor: expanded ? '#007bff' : '#f8f9fa',
              color: expanded ? 'white' : '#007bff',
              border: `2px solid ${expanded ? '#007bff' : '#007bff'}`,
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: 'bold',
              transition: 'all 0.2s ease',
              width: '100%'
            }}
          >
{expanded ? 'Hide Options' : 'Compare All Options'} ({ratesWithEffectiveCost.length} available)
          </button>
          
          {expanded && (
            <div style={{
              position: 'absolute',
              right: '0px',
              top: '100%',
              backgroundColor: 'white',
              border: '3px solid #007bff',
              borderRadius: '12px',
              boxShadow: '0 8px 24px rgba(0,123,255,0.15)',
              zIndex: 1000,
              minWidth: '500px',
              maxWidth: '600px',
              maxHeight: '500px',
              overflowY: 'auto',
              marginTop: '4px'
            }}>
              {/* Header */}
              <div style={{
                padding: '16px 20px',
                backgroundColor: '#007bff',
                color: 'white',
                fontWeight: 'bold',
                fontSize: '16px',
                borderRadius: '9px 9px 0 0',
                textAlign: 'center'
              }}>
Shipping Options for {locationName}
              </div>
              
              {/* Table Header */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: '2fr 1.5fr 1fr 1.2fr 1.2fr 1fr',
                gap: '8px',
                padding: '12px 16px',
                backgroundColor: '#f8f9fa',
                borderBottom: '2px solid #e9ecef',
                fontSize: '11px',
                fontWeight: 'bold',
                color: '#495057',
                position: 'sticky',
                top: '0',
                zIndex: 10
              }}>
                <div>CARRIER & SERVICE</div>
                <div>BROKERAGE</div>
                <div>TRANSIT</div>
                <div>SHIPPING</div>
                <div>TOTAL COST</div>
                <div>ACTION</div>
              </div>
              
              {ratesWithEffectiveCost.map((rate, index) => {
                const isSelected = selectedRate && 
                  rate.carrier_id === selectedRate.carrier_id && 
                  rate.service_id === selectedRate.service_id;
                const isCheapest = index === 0; // First rate is cheapest after sorting
                
                return (
                  <div
                    key={`${rate.carrier_id}-${rate.service_id}`}
                    onClick={() => onRateSelect && onRateSelect(shipmentId, rate)}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '2fr 1.5fr 1fr 1.2fr 1.2fr 1fr',
                      gap: '8px',
                      padding: '16px',
                      borderBottom: index < ratesWithEffectiveCost.length - 1 ? '1px solid #e9ecef' : 'none',
                      backgroundColor: isSelected ? '#f0f8ff' : isCheapest ? '#f8fff8' : 'white',
                      cursor: onRateSelect ? 'pointer' : 'default',
                      transition: 'all 0.2s ease',
                      fontSize: '13px'
                    }}
                    onMouseEnter={(e) => {
                      if (onRateSelect && !isSelected) {
                        e.currentTarget.style.backgroundColor = '#f0f0f0';
                        e.currentTarget.style.transform = 'scale(1.01)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (onRateSelect && !isSelected) {
                        e.currentTarget.style.backgroundColor = isCheapest ? '#f8fff8' : 'white';
                        e.currentTarget.style.transform = 'scale(1)';
                      }
                    }}
                  >
                    {/* Carrier & Service */}
                    <div style={{ fontWeight: '600', color: '#212529' }}>
                      <div style={{ fontSize: '14px', marginBottom: '2px' }}>
                        {rate.carrier_name}
                      </div>
                      <div style={{ fontSize: '12px', color: '#6c757d' }}>
                        {rate.service_name}
                      </div>
                      {isSelected && (
                        <span style={{ 
                          backgroundColor: '#007bff', 
                          color: 'white', 
                          padding: '2px 6px', 
                          borderRadius: '4px',
                          fontSize: '9px',
                          marginTop: '4px',
                          display: 'inline-block'
                        }}>
                          SELECTED
                        </span>
                      )}
                      {!isSelected && isCheapest && (
                        <span style={{ 
                          backgroundColor: '#28a745', 
                          color: 'white', 
                          padding: '2px 6px', 
                          borderRadius: '4px',
                          fontSize: '9px',
                          marginTop: '4px',
                          display: 'inline-block'
                        }}>
                          BEST VALUE
                        </span>
                      )}
                    </div>
                    
                    {/* Brokerage Status */}
                    <div>
                      {rate.brokerage_fees_included ? (
                        <span style={{ 
                          color: '#28a745', 
                          fontWeight: 'bold',
                          backgroundColor: '#d4edda',
                          padding: '4px 8px',
                          borderRadius: '6px',
                          fontSize: '11px'
                        }}>
                          INCLUDED
                        </span>
                      ) : (
                        <div>
                          <span style={{ 
                            color: '#dc3545', 
                            fontWeight: 'bold',
                            backgroundColor: '#f8d7da',
                            padding: '4px 8px',
                            borderRadius: '6px',
                            fontSize: '11px'
                          }}>
                            NOT INCLUDED
                          </span>
                          {rate.brokerageFeeUSD > 0 && (
                            <div style={{ fontSize: '10px', color: '#e67e22', marginTop: '4px' }}>
                              +${rate.brokerageFeeUSD.toFixed(2)} USD
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    
                    {/* Transit Time */}
                    <div style={{ fontWeight: '500', color: '#495057' }}>
                      {rate.transit_days} day{rate.transit_days !== 1 ? 's' : ''}
                    </div>
                    
                    {/* Shipping Cost Only */}
                    <div style={{ fontWeight: 'bold', color: '#007bff' }}>
                      <div style={{ fontSize: '10px', color: '#6c757d', marginBottom: '2px' }}>
                        Shipping Only:
                      </div>
                      {rate.formatted}
                    </div>
                    
                    {/* Total with Brokerage */}
                    <div style={{ 
                      fontWeight: 'bold', 
                      fontSize: '15px',
                      color: isCheapest ? '#28a745' : '#212529'
                    }}>
                      <div style={{ fontSize: '10px', color: '#6c757d', marginBottom: '2px' }}>
                        Total + Brokerage:
                      </div>
                      CAD {rate.effectiveTotalCost.toFixed(2)}
                      {isCheapest && (
                        <div style={{ fontSize: '9px', color: '#28a745', marginTop: '2px' }}>
                          LOWEST TOTAL
                        </div>
                      )}
                    </div>

                    {/* Add to Cart Button */}
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                      {onAddToCart && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation(); // Prevent row selection
                            onAddToCart(rate);
                          }}
                          style={{
                            backgroundColor: '#ff6b35',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            padding: '8px 12px',
                            fontSize: '11px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            boxShadow: '0 2px 4px rgba(255, 107, 53, 0.2)'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#e85a2b';
                            e.currentTarget.style.transform = 'translateY(-1px)';
                            e.currentTarget.style.boxShadow = '0 4px 8px rgba(255, 107, 53, 0.3)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#ff6b35';
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '0 2px 4px rgba(255, 107, 53, 0.2)';
                          }}
                        >
                          🛒 Add to Cart
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
              
              {/* Footer */}
              <div style={{
                padding: '12px 16px',
                backgroundColor: '#f8f9fa',
                borderTop: '1px solid #e9ecef',
                fontSize: '10px',
                color: '#6c757d',
                textAlign: 'center',
                borderRadius: '0 0 9px 9px'
              }}>
                Rates sorted by total effective cost (shipping + brokerage if not included) • Estimates only
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}


interface ManufacturingAddress {
  id?: number
  name: string
  street_address: string
  city: string
  state?: string
  country: string
  zip_code?: string
  reasoning?: string
}

interface StaffShipmentDetailsProps {
  shipment: Shipment
  clientId: number
  token: string
  onBack: () => void
}

export default function StaffShipmentDetails({ shipment, clientId, token, onBack }: StaffShipmentDetailsProps) {
  const [activeTab, setActiveTab] = useState<'overview' | 'boxes' | 'plans'>('overview')
  const [items, setItems] = useState<ShipmentItem[]>([])
  const [boxContents, setBoxContents] = useState<any[]>([])
  const [plans, setPlans] = useState<any[]>([])
  const [clientCountry, setClientCountry] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [boxesLoading, setBoxesLoading] = useState(true)
  const [plansLoading, setPlansLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [validationErrors, setValidationErrors] = useState<string[]>([])
  const [itemValidationErrors, setItemValidationErrors] = useState<{[key: string]: string[]}>({})
  const [manufacturingAddresses, setManufacturingAddresses] = useState<ManufacturingAddress[]>([])
  
  // Commercial Invoice Generation State
  const [showInvoiceDialog, setShowInvoiceDialog] = useState(false)
  const [invoicePassword, setInvoicePassword] = useState('')
  const [ltlType, setLtlType] = useState<'direct_amazon' | 'pattern_middle_mile' | 'ups_buffalo'>('direct_amazon')
  const [fcId, setFcId] = useState('')
  const [fcAddress, setFcAddress] = useState({
    street: '',
    city: '',
    state: '',
    zip: ''
  })
  const [generatingInvoice, setGeneratingInvoice] = useState(false)
  const [invoiceError, setInvoiceError] = useState<string | null>(null)
  
  // FDA Worksheet Generation State
  const [showFDADialog, setShowFDADialog] = useState(false)
  const [fdaPassword, setFdaPassword] = useState('')
  const [generatingFDA, setGeneratingFDA] = useState(false)
  const [fdaError, setFdaError] = useState<string | null>(null)
  const [selectedShippingRates, setSelectedShippingRates] = useState<{[shipmentId: string]: ShippingRate}>({})

  // CUSMA Certificate of Origin State
  const [cusmaEligibility, setCusmaEligibility] = useState<any>(null)
  const [cusmaForm, setCusmaForm] = useState<any>(null)
  const [showCusmaModal, setShowCusmaModal] = useState(false)
  const [cusmaLoading, setCusmaLoading] = useState(false)
  const [cusmaFilesRefresh, setCusmaFilesRefresh] = useState(0)

  // Duty tracking state
  const [itemDuties, setItemDuties] = useState<{[itemId: string]: number}>({})

  // Keep the staff view's COGS summary in lockstep with the client shipment view.
  // Staff item data is already normalized to CAD by the staff endpoint.
  const shipmentTotals = items.length > 0 && items.every(item => Number(item.declaredValue) > 0)
    ? (() => {
        const totalCAD = items.reduce((sum, item) => sum + (Number(item.declaredValue) || 0) * item.quantity, 0)
        const totalDutiesCAD = items.reduce((sum, item) => sum + (Number(itemDuties[item.id]) || 0) * item.quantity, 0)
        return { totalCAD, totalUSD: totalCAD * 0.74, totalDutiesCAD }
      })()
    : null

  // Password cache state
  const [cachedPasswordMinutes, setCachedPasswordMinutes] = useState(0)

  useEffect(() => {
    loadShipmentItems()
    // Load box contents and plans in parallel when component mounts
    loadBoxContents()
    loadPlans()
  }, [shipment.id])

  // Initialize passwords from cache on mount
  useEffect(() => {
    const cached = passwordCache.get()
    if (cached) {
      setInvoicePassword(cached)
      setFdaPassword(cached)
      setCachedPasswordMinutes(passwordCache.getRemainingMinutes())
    }
  }, [])

  useEffect(() => {
    if (items.length > 0) {
      loadManufacturingAddresses()
      checkCUSMAEligibility()
    }
  }, [items])

  const handleRateSelection = async (shipmentId: string, rate: ShippingRate) => {
    console.log(`Selected rate for shipment ${shipmentId}:`, rate);
    
    // Update local state immediately
    setSelectedShippingRates(prev => ({
      ...prev,
      [shipmentId]: rate
    }));

    // Save to backend
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/api/staff/clients/${clientId}/shipments/${shipment.id}/selected-shipping-rate`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            shipmentId,
            selectedRate: rate
          })
        }
      );

      if (!response.ok) {
        console.warn('Failed to save selected shipping rate to backend');
      }
    } catch (err) {
      console.warn('Error saving selected shipping rate:', err);
    }
  };

  const handleAddToFreightcomCart = async (rate: ShippingRate, placement?: any) => {
    try {
      console.log('🛒 Adding to FreightCom cart:', rate);
      console.log('🏗️ Using placement data:', placement);
      console.log('🏗️ Placement warehouse_id:', placement?.warehouse_id);
      console.log('🏗️ Placement location_address:', placement?.location_address);
      console.log('🏗️ Placement destination_address:', placement?.destination_address);
      
      // Use actual placement destination if available, otherwise fallback to mock data
      let amazonFbaInfo;
      if (placement && (placement.destination_address || placement.location_address)) {
        // Parse the location address properly
        const addressStr = placement.destination_address || placement.location_address;
        let parsedAddress;
        
        if (typeof addressStr === 'string') {
          // Parse address string like "165 GLENN FOX RD, NEW STANTON, PA 15672-9703"
          const parts = addressStr.split(',').map(part => part.trim());
          if (parts.length >= 3) {
            // Format: "Street, City, State ZIP"
            const lastPart = parts[parts.length - 1]; // "PA 15672-9703"
            const stateZipMatch = lastPart.match(/^([A-Z]{2})\s+(\d{5}(-\d{4})?)$/);
            if (stateZipMatch) {
              parsedAddress = {
                street: parts[0], // "165 GLENN FOX RD"
                city: parts[1], // "NEW STANTON"  
                state: stateZipMatch[1], // "PA"
                zip: stateZipMatch[2].split('-')[0], // "15672"
                country: "US"
              };
            } else {
              // Fallback parsing
              parsedAddress = {
                street: parts[0],
                city: parts[1] || "Unknown City",
                state: lastPart.substring(0, 2).toUpperCase() || "CA",
                zip: lastPart.match(/\d{5}/)?.[0] || "00000",
                country: "US"
              };
            }
          } else {
            parsedAddress = {
              street: addressStr,
              city: "Unknown City",
              state: "CA",
              zip: "00000", 
              country: "US"
            };
          }
        } else {
          parsedAddress = addressStr;
        }
        
        amazonFbaInfo = {
          name: `Amazon FC ${placement.warehouse_id}`,
          warehouse_id: placement.warehouse_id,
          address: parsedAddress
        };
        console.log('🏗️ Created amazonFbaInfo:', amazonFbaInfo);
      } else {
        console.log('🚨 Using fallback mock data because placement or address is missing');
        console.log('🚨 placement exists:', !!placement);
        console.log('🚨 has destination_address:', !!placement?.destination_address);
        console.log('🚨 has location_address:', !!placement?.location_address);
        // Fallback mock data
        amazonFbaInfo = {
          name: "Amazon Fulfillment Center",
          address: {
            street: "123 Fulfillment Way",
            city: "Niagara Falls",
            state: "NY",
            zip: "14304",
            country: "US"
          }
        };
      }

      // Use actual box data from placement if available
      let boxes;
      if (placement && placement.box_groups && placement.box_groups.length > 0) {
        boxes = placement.box_groups.map((group: any, index: number) => ({
          length: parseFloat(group.length) || 50,
          width: parseFloat(group.width) || 40,
          height: parseFloat(group.height) || 40,
          weight: parseFloat(group.weight) || 12,
          description: group.description || `Box Group ${index + 1}`,
          box_number: index + 1
        }));
      } else {
        // Fallback mock data
        boxes = [
          {
            length: 50, width: 40, height: 40,
            weight: 12,
            description: "Sample Package"
          }
        ];
      }

      const mockCustomsData = {
        source: "details",
        confirmed_cerscompliance: false,
        details: {
          tax_recipient: {
            type: "shipper",
            shipper_tax_identifier: "",
            receiver_tax_identifier: "",
            other_tax_identifier: "",
            name: "YYZ PREP",
            address: {
              address_line1: "25 NEWKIRK CT",
              address_line2: "UNIT 5",
              street_number: "",
              street_name: "",
              unit_number: "",
              city: "BRAMPTON",
              region: "ON",
              country: "CA",
              postal_code: "L6Z 0B5",
              validated: false
            },
            phone_number: {
              number: "6472500111",
              extension: ""
            },
            reason_for_export: "commercially-sold-goods",
            additional_remarks: ""
          },
          products: [
            {
              product_name: "Sample Product",
              weight: { value: 3, unit: "kg" },
              hscode: "3304.10.0000",
              country_of_origin: "CN",
              num_units: 1,
              unit_price: { currency: "CAD", value: "23.00" },
              description: "Sample product description"
            }
          ]
        }
      };

      // Add placement information to the rate object so backend can identify correct placement
      const rateWithPlacement = {
        ...rate,
        shipment_id: placement?.shipment_id,
        warehouse_id: placement?.warehouse_id
      };

      const response = await apiService.addToFreightcomCart(
        shipment.id,
        rateWithPlacement,
        amazonFbaInfo,
        boxes,
        mockCustomsData
      );

      if (response.success) {
        const locationInfo = placement ? ` for ${placement.warehouse_id}` : '';
        alert(`✅ Successfully added to FreightCom cart${locationInfo}!\n\nCart ID: ${response.cart_id}\nShipment ID: ${response.shipment_id}\nCarrier: ${response.carrier}\nService: ${response.service}\nDestination: ${amazonFbaInfo.name}`);
      } else {
        alert(`❌ Failed to add to cart: ${response.error}`);
      }
    } catch (error: any) {
      console.error('Error adding to FreightCom cart:', error);
      alert(`❌ Error: ${error.message}`);
    }
  };

  const loadShipmentItems = async () => {
    try {
      setError(null)
      setLoading(true)
      
      // Use staff API endpoint
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/api/staff/clients/${clientId}/shipments/${shipment.id}/items`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      )

      const data = await response.json()
      
      if (data.success) {
        setItems(data.data || [])
      } else {
        setError(data.error || 'Failed to load shipment items')
      }
    } catch (err) {
      console.error('Failed to load shipment items:', err)
      setError('Failed to connect to server. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const loadBoxContents = async () => {
    try {
      setBoxesLoading(true)
      
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/api/staff/clients/${clientId}/shipments/${shipment.id}/boxes`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      )

      const data = await response.json()
      
      if (data.success) {
        setBoxContents(data.data || [])
      } else {
        console.error('Failed to load box contents:', data.error)
        setBoxContents([])
      }
    } catch (err) {
      console.error('Failed to load box contents:', err)
      setBoxContents([])
    } finally {
      setBoxesLoading(false)
    }
  }

  const loadPlans = async () => {
    try {
      setPlansLoading(true)
      
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/api/staff/clients/${clientId}/shipments/${shipment.id}/plans`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      )

      const data = await response.json()
      
      if (data.success) {
        setPlans(data.data || [])
        setClientCountry(data.clientCountry || null)
        
        // Load selected shipping rates from the placement data
        const selectedRates: {[shipmentId: string]: ShippingRate} = {}
        for (const plan of data.data || []) {
          for (const option of plan.placement_options || []) {
            for (const placement of option.placements || []) {
              if (placement.selected_shipping_rate) {
                selectedRates[placement.shipment_id] = placement.selected_shipping_rate
              }
            }
          }
        }
        setSelectedShippingRates(selectedRates)
        
      } else {
        console.error('Failed to load plans:', data.error)
        setPlans([])
      }
    } catch (err) {
      console.error('Failed to load plans:', err)
      setPlans([])
    } finally {
      setPlansLoading(false)
    }
  }

  const loadManufacturingAddresses = async () => {
    try {
      // Load manufacturing addresses for the first item with an ASIN to get client addresses
      const itemWithAsin = items.find(item => item.asin)
      if (!itemWithAsin && items.length > 0) {
        // If no ASIN items, try with any item to get client data
        const firstItem = items[0]
        if (firstItem.asin) {
          const response = await apiService.getClientASINData(firstItem.asin, clientId.toString())
          if (response.success && response.data?.manufacturingAddresses) {
            setManufacturingAddresses(response.data.manufacturingAddresses)
          }
        }
      } else if (itemWithAsin) {
        const response = await apiService.getClientASINData(itemWithAsin.asin!, clientId.toString())
        if (response.success && response.data?.manufacturingAddresses) {
          setManufacturingAddresses(response.data.manufacturingAddresses)
        }
      }
    } catch (error) {
      console.error('Error loading manufacturing addresses:', error)
    }
  }

  const handleNewAddressSaved = async (newAddress: ManufacturingAddress & { id: number }) => {
    // Add the new address to the list immediately
    setManufacturingAddresses(prev => [...prev, newAddress])
  }

  const handleRunAILookup = async () => {
    try {
      setLoading(true)
      setError(null)
      
      console.log('🤖 Running AI lookup for shipment:', shipment.id)
      console.log('📦 Items to process:', items.length)
      
      let updatedItems = [...items]
      let addressesFound = 0
      
      for (let i = 0; i < items.length; i++) {
        const item = items[i]
        console.log(`🔍 Processing item ${i + 1}/${items.length}: ${item.description}`)
        
        try {
          // Use the existing API service method
          const response = await apiService.lookupManufacturingAddress(
            item.description, 
            item.asin || undefined,
            item.countryOfOrigin || undefined
          )
          
          if (response.success && response.data) {
            console.log(`✅ Found address for ${item.description}:`, response.data)
            
            // Update the item with the manufacturing address
            const addressData = response.data
            updatedItems[i] = {
              ...updatedItems[i],
              manufacturingAddress: {
                name: addressData.name,
                street_address: addressData.street_address,
                city: addressData.city,
                state: addressData.state || '',
                country: addressData.country,
                zip_code: addressData.zip_code || ''
              }
            }
            
            addressesFound++
          } else {
            console.log(`❌ No address found for ${item.description}:`, response.error)
          }
        } catch (itemError: any) {
          console.error(`❌ Error processing item ${item.description}:`, itemError)
        }
        
        // Add a small delay between requests to avoid overwhelming the API
        if (i < items.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 1000))
        }
      }
      
      // Update the items state with all the new addresses
      setItems(updatedItems)
      
      // Show success message
      if (addressesFound > 0) {
        alert(`✅ AI Lookup completed! Found manufacturing addresses for ${addressesFound} out of ${items.length} items.`)
      } else {
        alert('⚠️ AI Lookup completed, but no manufacturing addresses were found. The AI may need more specific product information or the products may not have sufficient brand data.')
      }
      
    } catch (error: any) {
      console.error('❌ AI Lookup error:', error)
      setError(`Failed to run AI lookup: ${error.message || 'Unknown error'}`)
    } finally {
      setLoading(false)
    }
  }

  const handleGenerateFDAWorksheets = async () => {
    try {
      setGeneratingFDA(true)
      setFdaError(null)

      // Cache the password for 2 hours
      passwordCache.set(fdaPassword)

      // Prepare shipment data with items (items already have manufacturingAddress)
      const shipmentData = {
        ...shipment,
        items: items
      }

      const response = await apiService.generateFDAWorksheets(
        shipmentData,
        fdaPassword
      )

      if (response.success) {
        // Download the generated FDA worksheets PDF file
        if (response.fileData) {
          // Convert base64 to blob and download
          const byteCharacters = atob(response.fileData)
          const byteNumbers = new Array(byteCharacters.length)
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i)
          }
          const byteArray = new Uint8Array(byteNumbers)
          const blob = new Blob([byteArray], { type: 'application/pdf' })
          
          const downloadUrl = window.URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.href = downloadUrl
          link.download = response.filename || 'FDA-Worksheets.pdf'
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          window.URL.revokeObjectURL(downloadUrl)
        }
        
        // Close the dialog and reset form
        setShowFDADialog(false)
        setFdaPassword('')
        
        alert(response.message || 'FDA worksheets generated successfully!')
      } else {
        setFdaError(response.error || 'Failed to generate FDA worksheets')
      }
    } catch (error: any) {
      console.error('FDA worksheet generation error:', error)
      setFdaError(error.message || 'Failed to generate FDA worksheets')
    } finally {
      setGeneratingFDA(false)
    }
  }

  const handleGenerateInvoice = async () => {
    try {
      setGeneratingInvoice(true)
      setInvoiceError(null)

      // Cache the password for 2 hours
      passwordCache.set(invoicePassword)

      // Validate FC info if Direct to Amazon LTL
      if (ltlType === 'direct_amazon') {
        if (!fcId.trim()) {
          setInvoiceError('FC ID is required for Direct to Amazon LTL')
          setGeneratingInvoice(false)
          return
        }
        if (!fcAddress.street.trim() || !fcAddress.city.trim() ||
            !fcAddress.state.trim() || !fcAddress.zip.trim()) {
          setInvoiceError('Complete FC address is required for Direct to Amazon LTL')
          setGeneratingInvoice(false)
          return
        }
      }

      // Validate all items have required fields: COGS, Country of Origin, HTS Code
      const itemsWithMissingFields: string[] = []
      for (const item of items) {
        const missingFields: string[] = []

        if (!item.declaredValue || item.declaredValue <= 0) {
          missingFields.push('COGS')
        }
        if (!item.countryOfOrigin) {
          missingFields.push('Country of Origin')
        }
        if (!item.htsCode || item.htsCode.replace(/\D/g, '').length !== 10) {
          missingFields.push('HTS Code (10 digits)')
        }

        if (missingFields.length > 0) {
          const itemName = item.asin || item.description?.substring(0, 30) || 'Unknown item'
          itemsWithMissingFields.push(`• ${itemName}: Missing ${missingFields.join(', ')}`)
        }
      }

      if (itemsWithMissingFields.length > 0) {
        setInvoiceError(`Cannot generate invoice. The following items are missing required fields:\n\n${itemsWithMissingFields.join('\n')}`)
        setGeneratingInvoice(false)
        return
      }

      const response = await apiService.generateInvoice(
        shipment,
        items,
        invoicePassword,
        ltlType,
        ltlType === 'direct_amazon' ? { fcId, fcAddress } : undefined
      )

      if (response.success) {
        // Download the generated invoice
        if (response.fileData) {
          // Convert base64 to blob and download
          const byteCharacters = atob(response.fileData)
          const byteNumbers = new Array(byteCharacters.length)
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i)
          }
          const byteArray = new Uint8Array(byteNumbers)
          const blob = new Blob([byteArray], { type: 'application/pdf' })
          
          const downloadUrl = window.URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.href = downloadUrl
          link.download = response.filename || `commercial-invoice-${response.invoiceNumber}.pdf`
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          window.URL.revokeObjectURL(downloadUrl)
        } else if (response.filename) {
          // Fallback to download endpoint
          await apiService.downloadInvoice(response.filename)
        }
        
        // Close the dialog and reset form
        setShowInvoiceDialog(false)
        setInvoicePassword('')
        setLtlType('direct_amazon')
        setFcId('')
        setFcAddress({ street: '', city: '', state: '', zip: '' })
        
        alert(`Commercial invoice generated successfully! (${response.invoiceNumber})`)
      } else {
        setInvoiceError(response.error || 'Failed to generate commercial invoice')
      }
    } catch (error: any) {
      console.error('Invoice generation error:', error)
      setInvoiceError(error.message || 'Failed to generate commercial invoice')
    } finally {
      setGeneratingInvoice(false)
    }
  }

  // CUSMA Functions
  const checkCUSMAEligibility = async () => {
    try {
      // Use staff-specific CUSMA eligibility endpoint with the clientId prop
      const response = await apiService.checkStaffCUSMAEligibility(clientId.toString(), shipment.id, items)
      if (response.success) {
        setCusmaEligibility(response)
        
        // If eligible, check if form already exists
        if (response.isEligible) {
          const formResponse = await apiService.getStaffCUSMAForm(clientId.toString(), shipment.id)
          if (formResponse.success && formResponse.formData) {
            setCusmaForm(formResponse.formData)
          }
        }
      }
    } catch (error) {
      console.error('Error checking CUSMA eligibility:', error)
    }
  }

  const handleCUSMAIndicatorClick = async () => {
    // Always regenerate form with current items data to ensure HTS codes are included
    try {
      setCusmaLoading(true)
      const response = await apiService.generateStaffCUSMAForm(clientId.toString(), shipment.id, items)
      if (response.success) {
        setCusmaForm(response.formData)
        setShowCusmaModal(true)
      } else {
        alert('Failed to generate CUSMA form: ' + response.error)
      }
    } catch (error: any) {
      alert('Error generating CUSMA form: ' + error.message)
    } finally {
      setCusmaLoading(false)
    }
  }

  const handleSaveCUSMAForm = async (formData: any) => {
    try {
      const response = await apiService.updateStaffCUSMAForm(clientId.toString(), shipment.id, formData.id, formData)
      if (response.success) {
        setCusmaForm(response.formData || formData)
        
        // Trigger CUSMA files refresh to update the checklist
        if (formData.status === 'signed') {
          setCusmaFilesRefresh(prev => prev + 1)
        }
        
        alert(formData.status === 'signed' ? 'CUSMA form completed and signed successfully!' : 'CUSMA form saved as draft.')
      } else {
        throw new Error(response.error || 'Failed to save form')
      }
    } catch (error: any) {
      throw error // Re-throw to let modal handle it
    }
  }

  const handleCUSMADownload = async () => {
    try {
      if (!cusmaForm || !cusmaForm.id) {
        alert('No CUSMA form available to download')
        return
      }

      // Get list of CUSMA PDFs for this client
      const response = await apiService.listS3CUSMAForms(clientId.toString())
      if (response.success && response.forms?.length > 0) {
        // Find the latest PDF for this shipment that's signed
        const shipmentPDFs = response.forms.filter((file: any) => 
          file.fileName.includes(`-${shipment.id}-`) && 
          file.fileName.includes('signed')
        )
        
        if (shipmentPDFs.length > 0) {
          // Get the most recent signed PDF
          const latestPDF = shipmentPDFs.sort((a, b) => 
            new Date(b.lastModified).getTime() - new Date(a.lastModified).getTime()
          )[0]
          
          const response = await apiService.downloadS3CUSMAForm(latestPDF.fileName, clientId.toString())
          if (response.ok) {
            const blob = await response.blob()
            const url = window.URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url
            link.download = `CUSMA-Certificate-${shipment.reference || shipment.id}-${new Date().toISOString().split('T')[0]}.pdf`
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            window.URL.revokeObjectURL(url)
          } else {
            throw new Error('Download failed')
          }
        } else {
          alert('No signed CUSMA certificate found for this shipment')
        }
      } else {
        alert('No CUSMA forms found in storage')
      }
    } catch (error: any) {
      console.error('Error downloading CUSMA form:', error)
      alert('Failed to download CUSMA form: ' + (error.message || 'Unknown error'))
    }
  }

  const getStatusBadge = (status: string) => {
    const baseStyle = {
      padding: '4px 8px',
      borderRadius: '4px',
      fontSize: '12px',
      fontWeight: 'bold',
      textTransform: 'uppercase' as const
    }

    switch (status) {
      case 'pending':
        return { ...baseStyle, backgroundColor: '#fff3cd', color: '#856404' }
      case 'declared':
        return { ...baseStyle, backgroundColor: '#d1ecf1', color: '#0c5460' }
      case 'invoiced':
        return { ...baseStyle, backgroundColor: '#d4edda', color: '#155724' }
      default:
        return { ...baseStyle, backgroundColor: '#f8f9fa', color: '#6c757d' }
    }
  }

  const getDestinationText = () => {
    if (shipment.destinationType === 'amazon_fba') {
      return shipment.destinationDetails?.fcCode 
        ? `Amazon FBA (${shipment.destinationDetails.fcCode})`
        : 'Amazon FBA'
    }
    return 'Pattern Shipping'
  }

  if (loading) {
    return (
      <div className="loading">
        <div>Loading shipment details...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="card">
        <button className="button secondary mb-4" onClick={onBack}>
          ← Back to Shipments
        </button>
        <div className="error">
          {error}
        </div>
        <button 
          className="button" 
          onClick={loadShipmentItems}
        >
          Retry
        </button>
      </div>
    )
  }

  return (
    <div className="shipment-details">
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      {/* Header */}
      <div className="card">
        <div className="flex justify-between items-center mb-4">
          <button className="button secondary" onClick={onBack}>
            ← Back to Shipments
          </button>
          <div style={getStatusBadge(shipment.status)}>
            {shipment.status}
          </div>
        </div>

        <h2 style={{ margin: '0 0 16px 0' }}>
          <a
            href={`https://app.yyzprep.ca/shipments/outbound/${shipment.id}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: '#007bff',
              textDecoration: 'none',
              cursor: 'pointer'
            }}
            onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
            onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
          >
            {shipment.reference}
          </a>
        </h2>

        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
          gap: '16px',
          marginBottom: '16px'
        }}>
          <div>
            <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
              CREATED
            </div>
            <div>
              {new Date(shipment.createdAt).toLocaleDateString()}
            </div>
          </div>

          {shipment.shippedAt && (
            <div>
              <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                SHIPPED
              </div>
              <div>
                {new Date(shipment.shippedAt).toLocaleDateString()}
              </div>
            </div>
          )}

          <div>
            <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
              DESTINATION
            </div>
            <div>
              {getDestinationText()}
            </div>
          </div>

          <div>
            <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
              ITEMS
            </div>
            <div>
              {items.length} item{items.length !== 1 ? 's' : ''}
            </div>
          </div>
        </div>

        {shipment.notes && (
          <div style={{ 
            padding: '12px',
            backgroundColor: '#f8f9fa',
            borderLeft: '4px solid #007bff',
            borderRadius: '4px'
          }}>
            <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
              NOTES
            </div>
            <div style={{ whiteSpace: 'pre-wrap', fontSize: '14px' }}>
              {shipment.notes}
            </div>
          </div>
        )}
      </div>

      {/* Tab Navigation */}
      <div className="card" style={{ padding: 0, marginBottom: '20px' }}>
        <div style={{ 
          display: 'flex', 
          borderBottom: '1px solid #dee2e6',
        }}>
          {[
            { key: 'overview', label: 'Shipment Overview' },
            { key: 'boxes', label: 'Box Contents', loading: boxesLoading },
            { key: 'plans', label: 'Plans', loading: plansLoading }
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key as 'overview' | 'boxes' | 'plans')}
              style={{
                padding: '15px 20px',
                border: 'none',
                backgroundColor: activeTab === tab.key ? '#f8f9fa' : 'white',
                color: activeTab === tab.key ? '#007bff' : '#666',
                fontWeight: activeTab === tab.key ? 'bold' : 'normal',
                borderBottom: activeTab === tab.key ? '2px solid #007bff' : '2px solid transparent',
                cursor: 'pointer',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              {tab.label}
              {tab.loading && (
                <div style={{
                  width: '16px',
                  height: '16px',
                  border: '2px solid #f3f3f3',
                  borderTop: '2px solid #007bff',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }} />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <>
          {/* Shipment Readiness Checklist */}
          <ShipmentChecklist 
            shipment={shipment}
            items={items}
            cusmaEligibility={cusmaEligibility}
            cusmaForm={cusmaForm}
            onCUSMAClick={handleCUSMAIndicatorClick}
            onCUSMADownload={handleCUSMADownload}
            cusmaFilesRefresh={cusmaFilesRefresh}
            isStaff={true}
            clientId={clientId.toString()}
          />


          {/* Validation Errors */}
      {validationErrors.length > 0 && (
        <div className="card" style={{ backgroundColor: '#f8d7da', border: '1px solid #f5c6cb', color: '#721c24' }}>
          {validationErrors.map((error, index) => (
            <div key={index} style={{ marginBottom: '8px', fontWeight: 'bold' }}>
              ⚠️ {error}
            </div>
          ))}
        </div>
      )}

      {/* Items */}
      <div className="card">
        <h3 style={{ margin: '0 0 20px 0' }}>
          Shipment Items
        </h3>

        {items.length === 0 ? (
          <div style={{ 
            textAlign: 'center', 
            padding: '40px', 
            color: '#666',
            fontStyle: 'italic'
          }}>
            No items found in this shipment.
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <div style={{ minWidth: '1360px' }}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: SHIPMENT_TABLE_GRID,
                backgroundColor: '#f8f9fa',
                borderBottom: '2px solid #dee2e6',
                padding: '8px 0',
                fontSize: '10px',
                fontWeight: 700,
                color: '#495057',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                <div style={{ padding: '0 6px' }}></div>
                <div style={{ padding: '0 6px' }}>Product</div>
                <div style={{ padding: '0 4px', textAlign: 'center' }}>Qty</div>
                <div style={{ padding: '0 4px' }}>Country</div>
                <div style={{ padding: '0 4px' }}>HTS Code</div>
                <div style={{ padding: '0 4px' }}>COGS (CAD)</div>
                <div style={{ padding: '0 4px' }}>Duty / SSB</div>
                <div style={{ padding: '0 4px' }}>Total Duties</div>
                <div style={{ padding: '0 4px' }}>Type / FDA</div>
                <div style={{ padding: '0 4px' }}>Description</div>
                <div style={{ padding: '0 4px' }}>Manufacturer</div>
                <div style={{ padding: '0 4px' }}>Food Reg ID</div>
                <div></div>
              </div>
            {items.map((item) => (
              <ShipmentItemRow
                key={item.id}
                item={item}
                shipmentId={shipment.id}
                manufacturingAddresses={manufacturingAddresses}
                validationErrors={itemValidationErrors[item.id] || []}
                isStaff={true}
                clientId={clientId.toString()}
                onDutyCalculated={(dutyAmount) => {
                  setItemDuties(prev => ({
                    ...prev,
                    [item.id]: dutyAmount
                  }))
                }}
                onUpdate={async (updates) => {
                  // Update the item in the items array immediately for UI responsiveness
                  setItems(prevItems =>
                    prevItems.map(prevItem =>
                      prevItem.id === item.id
                        ? { ...prevItem, ...updates }
                        : prevItem
                    )
                  )

                  // Save to backend database (staff uses staff endpoint with clientId)
                  try {
                    const endpoint = `/api/staff/clients/${clientId}/shipments/${shipment.id}/items/${item.id}`
                    const response = await apiService.put(endpoint, updates)
                    if (response.success) {
                      // OPTIMIZED: Only recheck CUSMA if country changed to/from CUSMA country
                      if (updates.countryOfOrigin) {
                        const newCountry = updates.countryOfOrigin?.trim().toUpperCase()
                        const oldCountry = item.countryOfOrigin?.trim().toUpperCase()

                        const isCUSMACountry = (country: string) =>
                          country === 'US' || country === 'USA' ||
                          country === 'CA' || country === 'CANADA' ||
                          country === 'MX' || country === 'MEXICO'

                        const statusChanged = isCUSMACountry(oldCountry || '') !== isCUSMACountry(newCountry || '')

                        if (statusChanged) {
                          // Use the simplified recheck function
                          setTimeout(() => recheckCUSMAEligibility(), 1000)
                        }
                      }
                    } else {
                      console.error('Failed to update item:', response.error)
                    }
                  } catch (error) {
                    console.error('Error updating item:', error)
                  }
                }}
                onNewAddressSaved={handleNewAddressSaved}
              />
            ))}
            </div>
          </div>
        )}
      </div>

      {shipmentTotals && (
        <div style={{ marginTop: '20px', padding: '16px', backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '4px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '16px', fontWeight: 'bold', marginBottom: shipmentTotals.totalDutiesCAD > 0 ? '12px' : 0 }}>
            <span>Total Shipment Value (COGS):</span>
            <div style={{ textAlign: 'right' }}>
              <div>${shipmentTotals.totalCAD.toFixed(2)} CAD</div>
              <div style={{ fontSize: '14px', color: '#666', fontWeight: 'normal' }}>(${shipmentTotals.totalUSD.toFixed(2)} USD)</div>
            </div>
          </div>
          {shipmentTotals.totalDutiesCAD > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '16px', fontWeight: 'bold', paddingTop: '12px', borderTop: '1px solid #dee2e6' }}>
              <span>SSB Total Duties + Tariffs:</span>
              <div style={{ textAlign: 'right' }}>
                <div style={{ color: '#dc2626' }}>${shipmentTotals.totalDutiesCAD.toFixed(2)} CAD</div>
                <div style={{ fontSize: '14px', color: '#666', fontWeight: 'normal' }}>({((shipmentTotals.totalDutiesCAD / shipmentTotals.totalCAD) * 100).toFixed(2)}% of COGS)</div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Action Buttons */}
      {items.length > 0 && (
        <div className="card">
          <div className="flex gap-4">
            <div>
              <button 
                className="button"
                onClick={() => setShowInvoiceDialog(true)}
              >
                Generate Commercial Invoice
              </button>
            </div>
            <div>
              <button 
                className="button"
                style={{ backgroundColor: '#28a745' }}
                onClick={() => setShowFDADialog(true)}
              >
                Generate FDA Worksheets
              </button>
            </div>
            
            {shipment.status === 'pending' && (
              <button 
                className="button secondary"
                onClick={handleRunAILookup}
                disabled={loading}
              >
                {loading ? 'Running AI Lookup...' : 'Run AI Lookup'}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Commercial Invoice Generation Dialog */}
      {showInvoiceDialog && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '24px',
            borderRadius: '8px',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
          }}>
            <h3 style={{ margin: '0 0 20px 0' }}>
              Generate Commercial Invoice
            </h3>

            {invoiceError && (
              <div style={{
                backgroundColor: '#f8d7da',
                border: '1px solid #f5c6cb',
                color: '#721c24',
                padding: '12px',
                borderRadius: '4px',
                marginBottom: '16px'
              }}>
                {invoiceError}
              </div>
            )}

            <div style={{ marginBottom: '16px' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px', 
                fontWeight: 'bold',
                fontSize: '14px'
              }}>
                LTL Type
              </label>
              <select
                value={ltlType}
                onChange={(e) => {
                  setLtlType(e.target.value as 'direct_amazon' | 'pattern_middle_mile' | 'ups_buffalo')
                  // Clear FC fields when switching away from direct_amazon
                  if (e.target.value !== 'direct_amazon') {
                    setFcId('')
                    setFcAddress({ street: '', city: '', state: '', zip: '' })
                  }
                }}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              >
                <option value="direct_amazon">Direct to Amazon LTL</option>
                <option value="pattern_middle_mile">Pattern Middle Mile LTL</option>
                <option value="ups_buffalo">UPS Buffalo Warehouse</option>
              </select>
              <div style={{
                fontSize: '12px',
                color: '#666',
                marginTop: '4px'
              }}>
                {ltlType === 'pattern_middle_mile'
                  ? 'Destination: Pattern Middle Mile - Hebron Warehouse, 2250 Progress Dr, Hebron, KY 41048'
                  : ltlType === 'ups_buffalo'
                  ? 'Destination: UPS Warehouse (EIN 58-2480149), 1907 James E Casey Drive, Buffalo, New York 14206, Tel: +1 888-742-5877'
                  : 'Destination: Amazon Fulfillment Center'}
              </div>
            </div>

            {ltlType === 'direct_amazon' && (
              <>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ 
                    display: 'block', 
                    marginBottom: '8px', 
                    fontWeight: 'bold',
                    fontSize: '14px'
                  }}>
                    Amazon FC ID <span style={{ color: 'red' }}>*</span>
                  </label>
                  <input
                    type="text"
                    value={fcId}
                    onChange={(e) => setFcId(e.target.value.toUpperCase())}
                    placeholder="e.g., MDW2, ONT8, etc."
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ 
                    display: 'block', 
                    marginBottom: '8px', 
                    fontWeight: 'bold',
                    fontSize: '14px'
                  }}>
                    FC Address <span style={{ color: 'red' }}>*</span>
                  </label>
                  <input
                    type="text"
                    value={fcAddress.street}
                    onChange={(e) => setFcAddress({ ...fcAddress, street: e.target.value })}
                    placeholder="Street Address"
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '14px',
                      marginBottom: '8px'
                    }}
                  />
                  <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '8px' }}>
                    <input
                      type="text"
                      value={fcAddress.city}
                      onChange={(e) => setFcAddress({ ...fcAddress, city: e.target.value })}
                      placeholder="City"
                      style={{
                        padding: '8px 12px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        fontSize: '14px'
                      }}
                    />
                    <input
                      type="text"
                      value={fcAddress.state}
                      onChange={(e) => setFcAddress({ ...fcAddress, state: e.target.value.toUpperCase() })}
                      placeholder="State"
                      maxLength={2}
                      style={{
                        padding: '8px 12px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        fontSize: '14px'
                      }}
                    />
                    <input
                      type="text"
                      value={fcAddress.zip}
                      onChange={(e) => setFcAddress({ ...fcAddress, zip: e.target.value })}
                      placeholder="ZIP"
                      style={{
                        padding: '8px 12px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                </div>
              </>
            )}

            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: 'bold',
                fontSize: '14px'
              }}>
                Password
                {cachedPasswordMinutes > 0 && (
                  <span style={{ fontWeight: 'normal', color: '#28a745', marginLeft: '8px', fontSize: '12px' }}>
                    (cached for {cachedPasswordMinutes} min)
                  </span>
                )}
              </label>
              <input
                type="password"
                value={invoicePassword}
                onChange={(e) => setInvoicePassword(e.target.value)}
                placeholder="Enter password to generate invoice"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: cachedPasswordMinutes > 0 ? '1px solid #28a745' : '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px',
                  backgroundColor: cachedPasswordMinutes > 0 ? '#f8fff8' : '#fff'
                }}
              />
            </div>

            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <button
                className="button secondary"
                onClick={() => {
                  setShowInvoiceDialog(false)
                  setInvoicePassword('')
                  setInvoiceError(null)
                  setFcId('')
                  setFcAddress({ street: '', city: '', state: '', zip: '' })
                }}
                disabled={generatingInvoice}
              >
                Cancel
              </button>
              <button
                className="button"
                onClick={handleGenerateInvoice}
                disabled={generatingInvoice || !invoicePassword.trim()}
              >
                {generatingInvoice ? 'Generating...' : 'Generate Invoice'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* FDA Worksheet Generation Dialog */}
      {showFDADialog && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '24px',
            borderRadius: '8px',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
          }}>
            <h3 style={{ margin: '0 0 20px 0' }}>
              Generate FDA Worksheets
            </h3>

            <div style={{
              backgroundColor: '#d4edda',
              border: '1px solid #c3e6cb',
              color: '#155724',
              padding: '12px',
              borderRadius: '4px',
              marginBottom: '16px',
              fontSize: '14px'
            }}>
              <strong>FDA Product Data Sheets</strong><br />
              This will generate FDA food import worksheets for all items in the shipment.
              Each product will have its own worksheet with:
              <ul style={{ marginTop: '8px', marginBottom: '0', paddingLeft: '20px' }}>
                <li>Product description and HTS codes</li>
                <li>Manufacturer information</li>
                <li>FDA compliance data</li>
                <li>Prior notice submitter details</li>
              </ul>
            </div>

            {fdaError && (
              <div style={{
                backgroundColor: '#f8d7da',
                border: '1px solid #f5c6cb',
                color: '#721c24',
                padding: '12px',
                borderRadius: '4px',
                marginBottom: '16px'
              }}>
                {fdaError}
              </div>
            )}

            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: 'bold',
                fontSize: '14px'
              }}>
                Password
                {cachedPasswordMinutes > 0 && (
                  <span style={{ fontWeight: 'normal', color: '#28a745', marginLeft: '8px', fontSize: '12px' }}>
                    (cached for {cachedPasswordMinutes} min)
                  </span>
                )}
              </label>
              <input
                type="password"
                value={fdaPassword}
                onChange={(e) => setFdaPassword(e.target.value)}
                placeholder="Enter password to generate FDA worksheets"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: cachedPasswordMinutes > 0 ? '1px solid #28a745' : '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px',
                  backgroundColor: cachedPasswordMinutes > 0 ? '#f8fff8' : '#fff'
                }}
              />
            </div>

            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <button
                className="button secondary"
                onClick={() => {
                  setShowFDADialog(false)
                  setFdaPassword('')
                  setFdaError(null)
                }}
                disabled={generatingFDA}
              >
                Cancel
              </button>
              <button
                className="button"
                style={{ backgroundColor: '#28a745' }}
                onClick={handleGenerateFDAWorksheets}
                disabled={generatingFDA || !fdaPassword.trim()}
              >
                {generatingFDA ? 'Generating...' : 'Generate FDA Worksheets'}
              </button>
            </div>
          </div>
        </div>
      )}
        </>
      )}

      {/* Box Contents Tab */}
      {activeTab === 'boxes' && (
        <div className="card">
          <h3 style={{ margin: '0 0 20px 0' }}>Box Contents</h3>
          
          {boxesLoading ? (
            <div style={{ 
              textAlign: 'center', 
              padding: '40px', 
              color: '#666'
            }}>
              <div style={{
                width: '24px',
                height: '24px',
                border: '3px solid #f3f3f3',
                borderTop: '3px solid #007bff',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '0 auto 10px'
              }} />
              Loading box contents...
            </div>
          ) : boxContents.length === 0 ? (
            <div style={{ 
              textAlign: 'center', 
              padding: '40px', 
              color: '#666',
              fontStyle: 'italic'
            }}>
              No box contents data available for this shipment.
            </div>
          ) : (
            <div className="card" style={{ padding: 0, overflow: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ backgroundColor: '#007bff', color: 'white', position: 'sticky', top: 0, zIndex: 10 }}>
                    <th style={{ 
                      padding: '12px 8px', 
                      textAlign: 'left', 
                      fontWeight: 'bold', 
                      fontSize: '12px',
                      borderRight: '1px solid rgba(255,255,255,0.2)',
                      minWidth: '80px'
                    }}>
                      BOX #
                    </th>
                    <th style={{ 
                      padding: '12px 8px', 
                      textAlign: 'center', 
                      fontWeight: 'bold', 
                      fontSize: '12px',
                      borderRight: '1px solid rgba(255,255,255,0.2)',
                      minWidth: '70px'
                    }}>
                      WEIGHT
                    </th>
                    <th style={{ 
                      padding: '12px 8px', 
                      textAlign: 'center', 
                      fontWeight: 'bold', 
                      fontSize: '12px',
                      borderRight: '1px solid rgba(255,255,255,0.2)',
                      minWidth: '120px'
                    }}>
                      DIMENSIONS
                    </th>
                    <th style={{ 
                      padding: '12px 8px', 
                      textAlign: 'left', 
                      fontWeight: 'bold', 
                      fontSize: '12px',
                      borderRight: '1px solid rgba(255,255,255,0.2)',
                      minWidth: '300px'
                    }}>
                      ITEM DESCRIPTION
                    </th>
                    <th style={{ 
                      padding: '12px 8px', 
                      textAlign: 'left', 
                      fontWeight: 'bold', 
                      fontSize: '12px',
                      borderRight: '1px solid rgba(255,255,255,0.2)',
                      minWidth: '100px'
                    }}>
                      SKU
                    </th>
                    <th style={{ 
                      padding: '12px 8px', 
                      textAlign: 'left', 
                      fontWeight: 'bold', 
                      fontSize: '12px',
                      borderRight: '1px solid rgba(255,255,255,0.2)',
                      minWidth: '80px'
                    }}>
                      ASIN
                    </th>
                    <th style={{ 
                      padding: '12px 8px', 
                      textAlign: 'left', 
                      fontWeight: 'bold', 
                      fontSize: '12px',
                      borderRight: '1px solid rgba(255,255,255,0.2)',
                      minWidth: '90px'
                    }}>
                      FNSKU
                    </th>
                    <th style={{ 
                      padding: '12px 8px', 
                      textAlign: 'center', 
                      fontWeight: 'bold', 
                      fontSize: '12px',
                      borderRight: '1px solid rgba(255,255,255,0.2)',
                      minWidth: '60px'
                    }}>
                      QTY
                    </th>
                    <th style={{ 
                      padding: '12px 8px', 
                      textAlign: 'right', 
                      fontWeight: 'bold', 
                      fontSize: '12px',
                      borderRight: '1px solid rgba(255,255,255,0.2)',
                      minWidth: '80px'
                    }}>
                      COGS/UNIT
                    </th>
                    <th style={{ 
                      padding: '12px 8px', 
                      textAlign: 'right', 
                      fontWeight: 'bold', 
                      fontSize: '12px',
                      minWidth: '100px'
                    }}>
                      EXTENDED COGS
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const allRows: any[] = [];
                    let totalShipmentCogs = 0;
                    let totalShipmentItems = 0;

                    // Group identical boxes
                    const boxGroups = new Map<string, any[]>();
                    
                    boxContents.forEach((box, index) => {
                      const weightNum = typeof box.weight === 'number' ? box.weight : parseFloat(box.weight?.toString() || '0');
                      const dimensions = box.dimensions?.toString() || '';
                      
                      const itemsSignature = box.items ? JSON.stringify(
                        box.items.map((item: any) => ({
                          sku: item.sku?.trim(),
                          asin: item.asin?.trim(),
                          quantity: item.quantity,
                          description: item.description?.trim()
                        })).sort((a: any, b: any) => (a.sku || '').localeCompare(b.sku || ''))
                      ) : '';

                      const weightKey = Math.round(weightNum * 100);
                      const groupKey = `${weightKey}_${dimensions}_${itemsSignature}`;

                      if (!boxGroups.has(groupKey)) {
                        boxGroups.set(groupKey, []);
                      }
                      boxGroups.get(groupKey)!.push(box);
                    });

                    // Process each group
                    Array.from(boxGroups.values()).forEach((boxes) => {
                      const boxNumbers = boxes.map(b => b.box_number || 0).sort((a, b) => a - b);
                      const isGroup = boxes.length > 1;
                      
                      // Create range display
                      let boxRange: string;
                      if (boxes.length === 1) {
                        boxRange = boxNumbers[0].toString();
                      } else if (boxNumbers.length > 2 && 
                                boxNumbers.every((num, idx) => idx === 0 || num === boxNumbers[idx - 1] + 1)) {
                        boxRange = `${boxNumbers[0]}-${boxNumbers[boxNumbers.length - 1]}`;
                      } else {
                        boxRange = boxNumbers.join(', ');
                      }

                      const weightNum = typeof boxes[0].weight === 'number' ? boxes[0].weight : parseFloat(boxes[0].weight?.toString() || '0');
                      const displayWeight = `${weightNum.toFixed(1)} kg`;
                      const dimensionsStr = boxes[0].dimensions?.toString() || 'N/A';
                      const displayDimensions = dimensionsStr === 'N/A' ? 'N/A' : `${dimensionsStr} cm`;

                      let groupTotal = 0;
                      let groupItemCount = 0;

                      // Add items for this group
                      if (boxes[0].items) {
                        boxes[0].items.forEach((item: any, itemIndex: number) => {
                          const cogsPerUnit = item.quantity > 0 ? (item.cogs / item.quantity) : 0;
                          const totalQuantity = item.quantity; // Show per-box quantity, not total across all boxes
                          const totalExtendedCogs = (item.cogs || 0) * boxes.length;
                          
                          groupTotal += totalExtendedCogs;
                          groupItemCount += item.quantity * boxes.length; // Total items across all boxes for group totals

                          allRows.push({
                            type: 'item',
                            boxRange: itemIndex === 0 ? boxRange : '',
                            weight: itemIndex === 0 ? displayWeight : '',
                            dimensions: itemIndex === 0 ? displayDimensions : '',
                            isGroupFirst: itemIndex === 0,
                            isGroup: isGroup,
                            groupCount: boxes.length,
                            item: item,
                            totalQuantity: totalQuantity,
                            totalExtendedCogs: totalExtendedCogs,
                            cogsPerUnit: cogsPerUnit,
                            isFirst: itemIndex === 0,
                            isLast: itemIndex === boxes[0].items.length - 1
                          });
                        });
                      }

                      // Add subtotal row for this group
                      allRows.push({
                        type: 'subtotal',
                        boxRange: boxRange,
                        isGroup: isGroup,
                        groupCount: boxes.length,
                        groupTotal: groupTotal,
                        groupItemCount: groupItemCount
                      });

                      totalShipmentCogs += groupTotal;
                      totalShipmentItems += groupItemCount;
                    });

                    // Add grand total row
                    allRows.push({
                      type: 'grandTotal',
                      totalShipmentCogs: totalShipmentCogs,
                      totalShipmentItems: totalShipmentItems,
                      totalBoxes: boxContents.length
                    });

                    return allRows;
                  })().map((row, rowIndex) => {
                    if (row.type === 'item') {
                      return (
                        <tr key={rowIndex} style={{ 
                          borderBottom: '1px solid #dee2e6',
                          backgroundColor: rowIndex % 2 === 0 ? '#ffffff' : '#f8f9fa'
                        }}>
                          <td style={{ 
                            padding: '8px', 
                            borderRight: '1px solid #dee2e6',
                            verticalAlign: 'top',
                            fontWeight: row.isGroupFirst ? 'bold' : 'normal',
                            color: row.isGroupFirst ? '#007bff' : '#333'
                          }}>
                            {row.boxRange}
                            {row.isGroupFirst && row.isGroup && (
                              <div style={{ fontSize: '10px', color: '#007bff', marginTop: '2px' }}>
                                ({row.groupCount} boxes)
                              </div>
                            )}
                          </td>
                          <td style={{ 
                            padding: '8px', 
                            textAlign: 'center', 
                            borderRight: '1px solid #dee2e6',
                            verticalAlign: 'top',
                            color: '#666'
                          }}>
                            {row.weight}
                          </td>
                          <td style={{ 
                            padding: '8px', 
                            textAlign: 'center', 
                            borderRight: '1px solid #dee2e6',
                            verticalAlign: 'top',
                            color: '#666'
                          }}>
                            {row.dimensions}
                          </td>
                          <td style={{ 
                            padding: '8px', 
                            borderRight: '1px solid #dee2e6',
                            verticalAlign: 'top'
                          }}>
                            <div style={{ fontWeight: '500' }}>{row.item.description}</div>
                            {row.isGroup && (
                              <div style={{ fontSize: '10px', color: '#007bff', marginTop: '1px' }}>
                                {row.item.quantity} per box × {row.groupCount} boxes
                              </div>
                            )}
                          </td>
                          <td style={{ 
                            padding: '8px', 
                            borderRight: '1px solid #dee2e6',
                            verticalAlign: 'top',
                            fontSize: '11px',
                            color: '#666'
                          }}>
                            {row.item.sku}
                          </td>
                          <td style={{ 
                            padding: '8px', 
                            borderRight: '1px solid #dee2e6',
                            verticalAlign: 'top',
                            fontSize: '11px',
                            color: '#666'
                          }}>
                            {row.item.asin}
                          </td>
                          <td style={{ 
                            padding: '8px', 
                            borderRight: '1px solid #dee2e6',
                            verticalAlign: 'top',
                            fontSize: '11px',
                            color: '#666'
                          }}>
                            {row.item.fnsku}
                          </td>
                          <td style={{ 
                            padding: '8px', 
                            textAlign: 'center', 
                            borderRight: '1px solid #dee2e6',
                            verticalAlign: 'top',
                            fontWeight: '500'
                          }}>
                            {row.totalQuantity}
                          </td>
                          <td style={{ 
                            padding: '8px', 
                            textAlign: 'right', 
                            borderRight: '1px solid #dee2e6',
                            verticalAlign: 'top',
                            color: '#666'
                          }}>
                            ${row.cogsPerUnit.toFixed(2)}
                          </td>
                          <td style={{ 
                            padding: '8px', 
                            textAlign: 'right',
                            verticalAlign: 'top',
                            fontWeight: '500'
                          }}>
                            ${row.totalExtendedCogs.toFixed(2)}
                          </td>
                        </tr>
                      );
                    } else if (row.type === 'subtotal') {
                      return (
                        <tr key={rowIndex} style={{ 
                          backgroundColor: '#e3f2fd',
                          borderTop: '2px solid #007bff',
                          borderBottom: '2px solid #007bff'
                        }}>
                          <td colSpan={7} style={{ 
                            padding: '10px 8px', 
                            fontWeight: 'bold',
                            color: '#007bff',
                            fontSize: '14px'
                          }}>
                            {row.isGroup ? `Boxes ${row.boxRange} Subtotal` : `Box ${row.boxRange} Subtotal`}
                            {row.isGroup && ` (${row.groupCount} identical boxes)`}
                          </td>
                          <td style={{ 
                            padding: '10px 8px', 
                            textAlign: 'center',
                            fontWeight: 'bold',
                            color: '#007bff',
                            borderRight: '1px solid #007bff'
                          }}>
                            {row.groupItemCount}
                          </td>
                          <td style={{ 
                            padding: '10px 8px', 
                            textAlign: 'right',
                            color: '#666',
                            borderRight: '1px solid #007bff'
                          }}>
                            -
                          </td>
                          <td style={{ 
                            padding: '10px 8px', 
                            textAlign: 'right',
                            fontWeight: 'bold',
                            color: '#007bff',
                            fontSize: '14px'
                          }}>
                            ${row.groupTotal.toFixed(2)}
                          </td>
                        </tr>
                      );
                    } else if (row.type === 'grandTotal') {
                      return (
                        <tr key={rowIndex} style={{ 
                          backgroundColor: '#f0f8ff',
                          borderTop: '3px solid #007bff'
                        }}>
                          <td colSpan={7} style={{ 
                            padding: '12px 8px', 
                            fontWeight: 'bold',
                            color: '#007bff',
                            fontSize: '16px'
                          }}>
                            SHIPMENT GRAND TOTAL ({row.totalBoxes} boxes)
                          </td>
                          <td style={{ 
                            padding: '12px 8px', 
                            textAlign: 'center',
                            fontWeight: 'bold',
                            color: '#007bff',
                            fontSize: '14px',
                            borderRight: '1px solid #007bff'
                          }}>
                            {row.totalShipmentItems}
                          </td>
                          <td style={{ 
                            padding: '12px 8px', 
                            textAlign: 'right',
                            color: '#666',
                            borderRight: '1px solid #007bff'
                          }}>
                            -
                          </td>
                          <td style={{ 
                            padding: '12px 8px', 
                            textAlign: 'right',
                            fontWeight: 'bold',
                            color: '#007bff',
                            fontSize: '16px'
                          }}>
                            ${row.totalShipmentCogs.toFixed(2)}
                          </td>
                        </tr>
                      );
                    }
                    return null;
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Plans Tab */}
      {activeTab === 'plans' && (
        <div className="card">
          <h3 style={{ margin: '0 0 20px 0' }}>FBA Placement Plans</h3>
          
          {plansLoading ? (
            <div style={{ 
              textAlign: 'center', 
              padding: '40px', 
              color: '#666'
            }}>
              <div style={{
                width: '24px',
                height: '24px',
                border: '3px solid #f3f3f3',
                borderTop: '3px solid #007bff',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '0 auto 10px'
              }} />
              Loading placement plans and shipping estimates...
            </div>
          ) : plans.length === 0 ? (
            <div style={{ 
              textAlign: 'center', 
              padding: '40px', 
              color: '#666',
              fontStyle: 'italic'
            }}>
              No placement plans available for this shipment.
            </div>
          ) : (
            <div>
              {plans.map((plan, planIndex) => (
                <div key={plan.id || planIndex} style={{ marginBottom: '20px' }}>
                  {/* Plan Header */}
                  <div className="card" style={{ backgroundColor: '#f0f8ff', border: '2px solid #007bff', marginBottom: '15px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                      <h4 style={{ margin: 0, color: '#007bff', fontSize: '16px' }}>
                        {plan.plan_name || `FBA Plan ${planIndex + 1}`}
                      </h4>
                      <div style={{ 
                        backgroundColor: plan.status === 'CONFIRMED' ? '#28a745' : '#6c757d',
                        color: 'white', 
                        padding: '4px 12px', 
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: 'bold'
                      }}>
                        {plan.status || 'PENDING'}
                      </div>
                    </div>
                    
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px', fontSize: '13px' }}>
                      <div><strong>Plan Type:</strong> {plan.plan_type || 'FBA Distribution'}</div>
                      <div><strong>Created:</strong> {plan.created_at ? new Date(plan.created_at).toLocaleDateString() : 'N/A'}</div>
                      <div><strong>Updated:</strong> {plan.updated_at ? new Date(plan.updated_at).toLocaleDateString() : 'N/A'}</div>
                      <div><strong>Placement Options:</strong> {plan.placement_options ? plan.placement_options.length : 0}</div>
                    </div>
                  </div>

                  {/* Placement Options */}
                  {plan.placement_options && plan.placement_options.length > 0 ? (
                    <div>
                      <h5 style={{ margin: '0 0 15px 0', color: '#333', fontSize: '14px' }}>Available Placement Options:</h5>
                      
                      {plan.placement_options.map((option: any, optionIndex: number) => (
                        <div key={option.id || optionIndex} className="card" style={{ 
                          marginBottom: '20px', 
                          backgroundColor: option.confirmed_at ? '#f0f8f0' : '#ffffff',
                          border: option.confirmed_at ? '2px solid #28a745' : '1px solid #dee2e6'
                        }}>
                          {/* Option Header */}
                          <div style={{ 
                            backgroundColor: '#f8f9fa', 
                            padding: '12px 15px', 
                            borderBottom: '1px solid #dee2e6',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}>
                            <div>
                              <strong style={{ color: '#007bff' }}>Placement Option {optionIndex + 1}</strong>
                              <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
                                Status: {option.status || 'PENDING'}
                                {option.confirmed_at && (
                                  <span> • Confirmed: {new Date(option.confirmed_at).toLocaleDateString()}</span>
                                )}
                              </div>
                            </div>
                            {option.status === 'CONFIRMED' && (
                              <div style={{ 
                                backgroundColor: '#28a745', 
                                color: 'white', 
                                padding: '2px 6px', 
                                borderRadius: '3px',
                                fontSize: '10px',
                                fontWeight: 'bold'
                              }}>
                                ACTIVE
                              </div>
                            )}
                          </div>

                          {/* Shipment Locations Table */}
                          {option.placements && option.placements.length > 0 && (
                            <div>
                              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                  <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '1px solid #dee2e6' }}>
                                    <th style={{ 
                                      padding: '12px 15px', 
                                      textAlign: 'left', 
                                      fontSize: '13px', 
                                      fontWeight: 'bold',
                                      color: '#495057',
                                      width: '30%'
                                    }}>
                                      DESTINATION
                                    </th>
                                    <th style={{ 
                                      padding: '12px 15px', 
                                      textAlign: 'center', 
                                      fontSize: '13px', 
                                      fontWeight: 'bold',
                                      color: '#495057',
                                      width: '12%'
                                    }}>
                                      BOXES
                                    </th>
                                    <th style={{ 
                                      padding: '12px 15px', 
                                      textAlign: 'center', 
                                      fontSize: '13px', 
                                      fontWeight: 'bold',
                                      color: '#495057',
                                      width: '12%'
                                    }}>
                                      ITEMS
                                    </th>
                                    <th style={{ 
                                      padding: '12px 15px', 
                                      textAlign: 'right', 
                                      fontSize: '13px', 
                                      fontWeight: 'bold',
                                      color: '#495057',
                                      width: '16%'
                                    }}>
                                      TOTAL COGS (USD)
                                    </th>
                                    <th style={{ 
                                      padding: '12px 15px', 
                                      textAlign: 'right', 
                                      fontSize: '13px', 
                                      fontWeight: 'bold',
                                      color: '#495057',
                                      width: '16%'
                                    }}>
                                      SHIPPING COST
                                    </th>
                                    {clientCountry === 'US' && (
                                      <th style={{
                                        padding: '12px 15px',
                                        textAlign: 'right',
                                        fontSize: '13px',
                                        fontWeight: 'bold',
                                        color: '#6f42c1',
                                        width: '14%'
                                      }}>
                                        PATTERN EST.
                                      </th>
                                    )}
                                    <th style={{
                                      padding: '12px 15px',
                                      textAlign: 'left',
                                      fontSize: '13px',
                                      fontWeight: 'bold',
                                      color: '#495057',
                                      width: clientCountry === 'US' ? '14%' : '24%'
                                    }}>
                                      BOX GROUPS
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {option.placements.map((placement: any, placementIndex: number) => (
                                    <tr key={placementIndex} style={{ 
                                      borderBottom: '1px solid #dee2e6',
                                      backgroundColor: placementIndex % 2 === 0 ? '#ffffff' : '#f8f9fa'
                                    }}>
                                      <td style={{ padding: '15px', fontSize: '14px', verticalAlign: 'top' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px' }}>
                                          <div style={{ fontWeight: '600', color: '#007bff', fontSize: '15px' }}>
                                            {placement.warehouse_id || 'Unknown FC'}
                                          </div>
                                          <span style={{
                                            backgroundColor: placement.status === 'CONFIRMED' ? '#d4edda' : '#fff3cd',
                                            color: placement.status === 'CONFIRMED' ? '#155724' : '#856404',
                                            padding: '3px 8px',
                                            borderRadius: '4px',
                                            fontSize: '11px',
                                            fontWeight: 'bold'
                                          }}>
                                            {placement.status || 'PENDING'}
                                          </span>
                                        </div>
                                        <div style={{ fontWeight: '500', marginBottom: '6px', fontSize: '14px' }}>
                                          {placement.location_name}
                                        </div>
                                        <div style={{ fontSize: '12px', color: '#666', lineHeight: '1.3' }}>
                                          {placement.location_address}
                                        </div>
                                        {placement.shipment_id && (
                                          <div style={{ fontSize: '11px', color: '#999', marginTop: '4px' }}>
                                            ID: {placement.shipment_id.substring(0, 8)}...
                                          </div>
                                        )}
                                      </td>
                                      <td style={{ 
                                        padding: '15px', 
                                        fontSize: '14px', 
                                        textAlign: 'center',
                                        fontWeight: '600',
                                        verticalAlign: 'top'
                                      }}>
                                        {placement.total_boxes || 0}
                                      </td>
                                      <td style={{ 
                                        padding: '15px', 
                                        fontSize: '14px', 
                                        textAlign: 'center',
                                        fontWeight: '600',
                                        verticalAlign: 'top'
                                      }}>
                                        {placement.total_items || 0}
                                      </td>
                                      <td style={{ 
                                        padding: '15px', 
                                        fontSize: '14px', 
                                        textAlign: 'right',
                                        fontWeight: 'bold',
                                        color: '#007bff',
                                        verticalAlign: 'top'
                                      }}>
                                        ${(placement.total_cogs || 0).toFixed(2)} USD
                                      </td>
                                      <td style={{ 
                                        padding: '15px', 
                                        fontSize: '14px', 
                                        textAlign: 'right',
                                        fontWeight: 'bold',
                                        color: '#28a745',
                                        verticalAlign: 'top',
                                        position: 'relative'
                                      }}>
                                        {placement.shipping_estimate ? (
                                          <ShippingRatesDisplay 
                                            cheapestRate={placement.shipping_estimate}
                                            allRates={placement.shipping_rates || []}
                                            locationName={placement.warehouse_id}
                                            shipmentId={placement.shipment_id}
                                            selectedRate={selectedShippingRates[placement.shipment_id]}
                                            onRateSelect={handleRateSelection}
                                            onAddToCart={(rate) => handleAddToFreightcomCart(rate, placement)}
                                            cogsValueUSD={placement.total_cogs || 0}
                                            lineItems={placement.total_items || 5}
                                            hasFood={items.some(item => {
                                              // Check if any item has food-related HTS codes
                                              if (!item.htsCode) return false
                                              const code = item.htsCode.substring(0, 2)
                                              // Common food HTS chapters: 04 (dairy), 16 (meat prep), 19 (cereal prep), 20 (vegetables), 21 (misc food)
                                              return ['04', '16', '19', '20', '21'].includes(code)
                                            })}
                                          />
                                        ) : (
                                          <div style={{ fontSize: '13px', color: '#999', fontStyle: 'italic' }}>
                                            Estimating...
                                          </div>
                                        )}
                                      </td>
                                      {clientCountry === 'US' && (() => {
                                        const estimate = calculatePatternEstimate(
                                          placement.warehouse_id,
                                          placement.box_groups || []
                                        )
                                        return (
                                          <td style={{
                                            padding: '15px',
                                            fontSize: '14px',
                                            textAlign: 'right',
                                            verticalAlign: 'top'
                                          }}>
                                            {estimate ? (
                                              <div>
                                                <div style={{ fontWeight: 'bold', color: '#6f42c1', fontSize: '15px' }}>
                                                  ${estimate.totalCost.toFixed(2)}
                                                </div>
                                                <div style={{ fontSize: '11px', color: '#666', marginTop: '4px' }}>
                                                  {estimate.zone} ({estimate.rateKey}) @ ${estimate.ratePerCuFt.toFixed(2)}/cu ft
                                                </div>
                                                <div style={{ fontSize: '11px', color: '#666' }}>
                                                  {estimate.totalCuFt.toFixed(2)} cu ft | {estimate.totalWeightLbs.toFixed(1)} lbs
                                                </div>
                                                <div style={{ fontSize: '11px', color: '#666' }}>
                                                  ${estimate.costPerLb.toFixed(2)}/lb | ${estimate.costPerBox.toFixed(2)}/box
                                                </div>
                                                {estimate.isOversized && (
                                                  <div style={{
                                                    fontSize: '10px',
                                                    color: '#fff',
                                                    backgroundColor: '#dc3545',
                                                    display: 'inline-block',
                                                    padding: '1px 6px',
                                                    borderRadius: '3px',
                                                    marginTop: '4px'
                                                  }}>OVERSIZED</div>
                                                )}
                                              </div>
                                            ) : (
                                              <span style={{ color: '#999', fontSize: '12px' }}>Unknown FC</span>
                                            )}
                                          </td>
                                        )
                                      })()}
                                      <td style={{
                                        padding: '15px',
                                        fontSize: '13px',
                                        color: '#666',
                                        verticalAlign: 'top'
                                      }}>
                                        {placement.box_groups ? placement.box_groups.map((group: any, groupIndex: number) => {
                                          const lengthCm = group.length_mm ? (group.length_mm / 10).toFixed(0) : '30';
                                          const widthCm = group.width_mm ? (group.width_mm / 10).toFixed(0) : '30';
                                          const heightCm = group.height_mm ? (group.height_mm / 10).toFixed(0) : '30';
                                          const weightKg = group.weight_gm ? (group.weight_gm / 1000).toFixed(1) : '1.0';
                                          // Calculate actual box value based on placement total_cogs
                                          let boxValue = 0;
                                          if (group.value_per_box_usd) {
                                            boxValue = group.value_per_box_usd;
                                          } else if (group.total_value_usd && group.number_of_boxes) {
                                            boxValue = group.total_value_usd / group.number_of_boxes;
                                          } else if (placement.total_cogs && placement.box_groups) {
                                            // Calculate total number of boxes in this placement
                                            const totalBoxesInPlacement = placement.box_groups.reduce((sum: number, bg: any) => {
                                              return sum + (bg.number_of_boxes || 1);
                                            }, 0);
                                            
                                            // Calculate value per box based on placement total COGS
                                            if (totalBoxesInPlacement > 0) {
                                              boxValue = placement.total_cogs / totalBoxesInPlacement;
                                            }
                                          }
                                          
                                          // Debug logging
                                          console.log(`📦 Box Group ${groupIndex + 1}:`, {
                                            placement_total_cogs: placement.total_cogs,
                                            total_box_groups: placement.box_groups?.length,
                                            this_group_boxes: group.number_of_boxes,
                                            calculated_boxValue: boxValue
                                          });
                                          
                                          // Only show value if we have actual data, no fallback
                                          const displayValue = boxValue > 0 ? `$${boxValue.toFixed(2)} USD each` : 'Value not available';
                                          
                                          return (
                                            <div key={groupIndex} style={{ marginBottom: '8px', padding: '6px', backgroundColor: '#f8f9fa', borderRadius: '4px', lineHeight: '1.4' }}>
                                              <div style={{ fontWeight: '600', fontSize: '13px', color: '#333' }}>
                                                {lengthCm}×{widthCm}×{heightCm}cm ({weightKg}kg)
                                              </div>
                                              <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
                                                {group.number_of_boxes} box{group.number_of_boxes !== 1 ? 'es' : ''} • {displayValue}
                                              </div>
                                            </div>
                                          );
                                        }) : 'No box data'}
                                      </td>
                                    </tr>
                                  ))}
                                  
                                  {/* Option Totals Row */}
                                  <tr style={{ 
                                    backgroundColor: '#e3f2fd',
                                    borderTop: '2px solid #007bff',
                                    fontWeight: 'bold'
                                  }}>
                                    <td style={{ padding: '15px', fontSize: '14px', color: '#007bff', fontWeight: 'bold' }}>
                                      Option {optionIndex + 1} Totals ({option.placements.length} destination{option.placements.length !== 1 ? 's' : ''})
                                    </td>
                                    <td style={{ padding: '15px', textAlign: 'center', fontSize: '14px', color: '#007bff', fontWeight: 'bold' }}>
                                      {option.placements.reduce((sum: number, p: any) => sum + (p.total_boxes || 0), 0)}
                                    </td>
                                    <td style={{ padding: '15px', textAlign: 'center', fontSize: '14px', color: '#007bff', fontWeight: 'bold' }}>
                                      {option.placements.reduce((sum: number, p: any) => sum + (p.total_items || 0), 0)}
                                    </td>
                                    <td style={{ padding: '15px', textAlign: 'right', fontSize: '14px', color: '#007bff', fontWeight: 'bold' }}>
                                      ${option.placements.reduce((sum: number, p: any) => sum + (p.total_cogs || 0), 0).toFixed(2)} USD
                                    </td>
                                    <td style={{ padding: '15px', textAlign: 'right', fontSize: '14px', color: '#28a745', fontWeight: 'bold' }}>
                                      {(() => {
                                        const totalShipping = option.placements.reduce((sum: number, p: any) => {
                                          return sum + (p.shipping_estimate?.total || 0);
                                        }, 0);
                                        const currency = option.placements.find((p: any) => p.shipping_estimate?.currency)?.shipping_estimate?.currency || 'CAD';
                                        return totalShipping > 0 ? `${currency} ${totalShipping.toFixed(2)}` : 'Calculating...';
                                      })()}
                                    </td>
                                    {clientCountry === 'US' && (() => {
                                      let totalPatternCost = 0
                                      let totalPatternCuFt = 0
                                      let totalPatternLbs = 0
                                      let totalPatternBoxes = 0
                                      for (const p of option.placements) {
                                        const est = calculatePatternEstimate(p.warehouse_id, p.box_groups || [])
                                        if (est) {
                                          totalPatternCost += est.totalCost
                                          totalPatternCuFt += est.totalCuFt
                                          totalPatternLbs += est.totalWeightLbs
                                          totalPatternBoxes += est.totalBoxes
                                        }
                                      }
                                      return (
                                        <td style={{ padding: '15px', textAlign: 'right', fontSize: '14px', color: '#6f42c1', fontWeight: 'bold' }}>
                                          <div>${totalPatternCost.toFixed(2)}</div>
                                          <div style={{ fontSize: '11px', fontWeight: 'normal', color: '#666' }}>
                                            {totalPatternCuFt.toFixed(2)} cu ft | {totalPatternLbs.toFixed(1)} lbs
                                          </div>
                                          <div style={{ fontSize: '11px', fontWeight: 'normal', color: '#666' }}>
                                            ${totalPatternLbs > 0 ? (totalPatternCost / totalPatternLbs).toFixed(2) : '0.00'}/lb | ${totalPatternBoxes > 0 ? (totalPatternCost / totalPatternBoxes).toFixed(2) : '0.00'}/box
                                          </div>
                                        </td>
                                      )
                                    })()}
                                    <td style={{ padding: '15px', fontSize: '12px', color: '#666' }}>
                                      {option.placements.reduce((sum: number, p: any) => sum + (p.box_groups?.length || 0), 0)} group{option.placements.reduce((sum: number, p: any) => sum + (p.box_groups?.length || 0), 0) !== 1 ? 's' : ''}
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="card" style={{ textAlign: 'center', padding: '30px', color: '#666' }}>
                      No placement options available for this plan.
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* CUSMA Form Modal */}
      {showCusmaModal && (
        <CUSMAFormModal
          isOpen={showCusmaModal}
          onClose={() => setShowCusmaModal(false)}
          shipmentId={shipment.id}
          items={items}
          isStaff={true}
          clientId={clientId}
        />
      )}
    </div>
  )
}
